/**
 * Receipt 뷰 normalization helper (#336 C).
 *
 * `OrderReceiptData` 의 모든 optional 필드를 boundary 1곳에서 fallback 적용 →
 * 자식 컴포넌트는 `??` 체인 없이 직접 사용.
 *
 * `OrderReceipt` discriminated union (#301 Phase 2) 의 *complete* 분기는
 * 모든 금액 필드 required 이므로 narrowing 시 `??` 자체가 불필요. 본 helper
 * 는 legacy `OrderReceiptData` 입력도 지원해 점진 마이그레이션 BC 보장.
 *
 * @see menu#336 — Phase 1~4 인프라 점진 마이그레이션
 */

import type { OrderReceipt, OrderReceiptData } from '@/types/order';
import type { CartItem } from '@/types';
import { deriveNetSubtotal } from '@/utils/pricing';

/** 자식 컴포넌트가 사용하는 정규화된 영수증 뷰 — 모든 금액 / 표시 필드 required */
export interface NormalizedReceiptView {
  orderId: string;
  paymentMethod: string;
  netSubtotal: number;
  grossSubtotal: number;
  tax: number;
  tip: number;
  total: number;
  pointsUsed: number;
  taxRate: number | undefined;
  tableNumber: string | undefined;
  /** 결제 시점 timestamp — undefined 허용 (formatDate / formatTime 가 처리) */
  timestamp: Date | undefined;
  /** 영수증 라인 아이템 — fallback 시 빈 배열 */
  items: CartItem[];
}

/**
 * `OrderReceiptData` 또는 `OrderReceipt` 입력 → 자식이 직접 사용 가능한
 * normalized view 로 변환. `??` fallback 을 본 함수 한 곳에 집중.
 */
export function narrowReceiptView(
  data: OrderReceiptData | OrderReceipt
): NormalizedReceiptView {
  if ('status' in data && data.status === 'complete') {
    // OrderReceiptComplete — 모든 금액 필드 required + Branded
    return {
      orderId: data.orderId,
      paymentMethod: data.paymentMethod,
      netSubtotal: data.netSubtotal as unknown as number,
      grossSubtotal: data.grossSubtotal as unknown as number,
      tax: data.tax as unknown as number,
      tip: data.tip as unknown as number,
      total: data.total as unknown as number,
      pointsUsed: data.pointsUsed ?? 0,
      taxRate: data.taxRate,
      tableNumber: data.tableNumber,
      timestamp: data.timestamp,
      items: data.items,
    };
  }

  // OrderReceiptPending / OrderReceiptData (legacy) — sparse → fallback 적용
  const legacy = data as OrderReceiptData & Partial<OrderReceipt>;
  const tax = legacy.tax ?? 0;
  const grossSubtotal = legacy.grossSubtotal ?? 0;
  const netSubtotal = legacy.netSubtotal ?? deriveNetSubtotal(grossSubtotal, tax);
  return {
    orderId: legacy.orderId ?? '',
    paymentMethod: legacy.paymentMethod ?? '',
    netSubtotal,
    grossSubtotal,
    tax,
    tip: legacy.tip ?? 0,
    total: legacy.total ?? 0,
    pointsUsed: legacy.pointsUsed ?? 0,
    taxRate: legacy.taxRate,
    tableNumber: legacy.tableNumber,
    timestamp: legacy.timestamp,
    items: legacy.items ?? [],
  };
}
